/**
 * Marca de la empresa en uso (nombre, colores y logo que el dueño elige en el portal, en Marca).
 *
 * La web es la misma para todas las empresas: la marca no viene en la compilación, se pide al API con el
 * código de la empresa (`GET company-brand?company=<código>`), antes de ingresar. Se guarda en este navegador
 * para pintar la pantalla al instante la próxima vez, y se actualiza en segundo plano.
 * Sin empresa elegida (o si el API no responde) se usa la marca de la compilación y, si no hay, la de Ruttia.
 */
import axios from 'axios';
import { useSyncExternalStore } from 'react';

export interface CompanyBrand {
  code: string;
  name: string;
  primary: string;
  secondary: string;
  logoUrl: string | null;
}

const STORAGE_KEY = 'ruttia:company-brand';
const isHex = (v: unknown) => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v);

const valid = (v: any): CompanyBrand | null =>
  v && typeof v.code === 'string' && typeof v.name === 'string' && isHex(v.primary) && isHex(v.secondary)
    ? {
        code: v.code,
        name: v.name,
        primary: v.primary,
        secondary: v.secondary,
        logoUrl: typeof v.logoUrl === 'string' && /^https:\/\//.test(v.logoUrl) ? v.logoUrl : null,
      }
    : null;

const readStored = (): CompanyBrand | null => {
  try {
    return valid(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return null;
  }
};

let current: CompanyBrand | null = readStored();
const listeners = new Set<() => void>();

const set = (brand: CompanyBrand | null) => {
  current = brand;
  try {
    if (brand) localStorage.setItem(STORAGE_KEY, JSON.stringify(brand));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* sin almacenamiento: solo dura hasta recargar */
  }
  listeners.forEach((fn) => fn());
};

export const getCompanyBrand = () => current;

export const subscribeCompanyBrand = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/** Marca de la empresa en uso; la pantalla se vuelve a pintar cuando llega o cambia. */
export const useCompanyBrand = () => useSyncExternalStore(subscribeCompanyBrand, getCompanyBrand, getCompanyBrand);

/**
 * Trae la marca de la empresa. Si guardada es de otra empresa, se quita de inmediato para no mostrar una marca
 * ajena mientras llega la nueva. Un 404 (la empresa no existe) la quita; un error de red deja la guardada.
 */
export const loadCompanyBrand = async (code: string | null | undefined) => {
  const wanted = (code || '').trim().toLowerCase();
  if (!wanted) return set(null);
  if (current && current.code !== wanted) set(null);
  try {
    const { data } = await axios.get('company-brand', { params: { company: wanted }, timeout: 8000 });
    const brand = valid(data);
    set(brand && brand.code === wanted ? brand : null);
  } catch (e: any) {
    if (e?.response?.status === 404) set(null);
  }
};

/** Al cambiar de empresa o al salir sin empresa recordada. */
export const clearCompanyBrand = () => set(null);

/** Iniciales para el distintivo cuando la empresa no tiene logo: «Inversiones Prado» → «IP». */
export const brandInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[0-9A-Za-zÀ-ÖØ-öø-ÿ]/.test(w))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '?';
